/**
 * CyclesPage — the cycle-count cockpit (INV-8 / NOVA-024). A Magasinier opens a count over a scope
 * (global | discipline | reactif), which snapshots every in-scope actif lot into frozen lignes capturing
 * the system quantity. The counter records the physical count per ligne (the ecart = comptee − systeme is
 * surfaced live), moves the session to review, then posts: the server appends ONE signed ajustement per
 * varying lot via the ledger so quantite_restante lands exactly on the counted value. There is no un-post
 * — a correction is a fresh count + a compensating ajustement. cancel aborts a not-yet-posted session.
 *
 * All actions are gated inventory.inventaire.run (server re-checks every request — useCan only hides UI).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  OfflineState,
  Select,
  Table,
  useToast,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import {
  useCancelInventaire,
  useCountInventaire,
  useLabId,
  useOpenInventaire,
  usePostInventaire,
  useReactifs,
  useReviewInventaire,
} from "../api";
import { reactifOptions, parseQty, fmtQty } from "../lib";
import { idStr } from "../types";
import type {
  AjustementInventaire,
  InventaireScope,
  LigneInventaire,
  SessionInventaire,
} from "../types";
import { PageHeader } from "../components/PageHeader";

const SCOPES: ReadonlyArray<InventaireScope> = ["global", "discipline", "reactif"];

/** Tone + glyph for a session statut chip (shape carries meaning, not color alone). */
function statutVisual(statut: string): { tone: "info" | "warn" | "success" | "danger"; glyph: string } {
  switch (statut) {
    case "en_cours":
      return { tone: "info", glyph: "●" };
    case "en_revue":
      return { tone: "warn", glyph: "▲" };
    case "postee":
      return { tone: "success", glyph: "✓" };
    case "annulee":
      return { tone: "danger", glyph: "✕" };
    default:
      return { tone: "info", glyph: "●" };
  }
}

export function CyclesPage() {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);
  const canRun = useCan(INV_CAPS.inventaireRun);

  const catalogQuery = useReactifs(labId, canRead && online && canRun);
  const reactifs = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const options = useMemo(() => reactifOptions(reactifs), [reactifs]);

  // The active session lives in component state — opening/counting/reviewing all return the fresh session.
  const [session, setSession] = useState<SessionInventaire | null>(null);
  const [postSummary, setPostSummary] = useState<AjustementInventaire[] | null>(null);
  const [postNoop, setPostNoop] = useState(false);

  // Open form
  const [libelle, setLibelle] = useState("");
  const [scope, setScope] = useState<InventaireScope>("global");
  const [reactifId, setReactifId] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [touched, setTouched] = useState(false);

  // Per-ligne count drafts (ligne_id → editable string).
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const openMut = useOpenInventaire(labId);
  const countMut = useCountInventaire(labId);
  const reviewMut = useReviewInventaire(labId);
  const postMut = usePostInventaire(labId);
  const cancelMut = useCancelInventaire(labId);

  const libelleError = touched && libelle.trim() === "" ? t("inventory.field.required") : null;
  const reactifError = touched && scope === "reactif" && reactifId === "" ? t("inventory.field.required") : null;
  const disciplineError = touched && scope === "discipline" && discipline.trim() === "" ? t("inventory.field.required") : null;
  const openValid =
    libelle.trim() !== "" &&
    (scope !== "reactif" || reactifId !== "") &&
    (scope !== "discipline" || discipline.trim() !== "");

  const resetOpenForm = () => {
    setLibelle("");
    setScope("global");
    setReactifId("");
    setDiscipline("");
    setTouched(false);
  };

  const submitOpen = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!openValid || !canRun) return;
    openMut.mutate(
      {
        libelle: libelle.trim(),
        scope,
        ...(scope === "discipline" ? { discipline_code: discipline.trim() } : {}),
        ...(scope === "reactif" ? { reactif_id: reactifId } : {}),
      },
      {
        onSuccess: (s) => {
          setSession(s);
          setDrafts({});
          setPostSummary(null);
          setPostNoop(false);
          resetOpenForm();
          toast.success(t("inventory.cycles.open.success", { n: s.lignes.length }));
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  const isTerminal = session != null && ["postee", "annulee"].includes(session.statut);
  const canEditCounts = session != null && !isTerminal && canRun;

  const submitCount = (ligne: LigneInventaire) => {
    if (!session || !canEditCounts) return;
    const raw = drafts[ligne.ligne_id] ?? "";
    const parsed = parseQty(raw);
    if (parsed == null) {
      toast.error(t("inventory.field.qtyInvalid"));
      return;
    }
    countMut.mutate(
      { sessionId: session.session_id, ligne_id: ligne.ligne_id, quantite_comptee: parsed },
      {
        onSuccess: (s) => {
          setSession(s);
          setDrafts((d) => {
            const next = { ...d };
            delete next[ligne.ligne_id];
            return next;
          });
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.qtyInvalid")),
      },
    );
  };

  const submitReview = () => {
    if (!session) return;
    reviewMut.mutate(
      { sessionId: session.session_id },
      {
        onSuccess: (s) => {
          setSession(s);
          toast.success(t("inventory.cycles.review.success"));
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  const submitPost = () => {
    if (!session) return;
    postMut.mutate(
      { sessionId: session.session_id },
      {
        onSuccess: (res) => {
          setPostSummary(res.ajustements);
          setPostNoop(res.idempotent_noop);
          setSession((cur) => (cur ? { ...cur, statut: res.statut } : cur));
          if (res.idempotent_noop) toast.info(t("inventory.cycles.post.noop"));
          else toast.success(t("inventory.cycles.post.success", { n: res.ajustements.length }));
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  const submitCancel = () => {
    if (!session) return;
    cancelMut.mutate(
      { sessionId: session.session_id },
      {
        onSuccess: (s) => {
          setSession(s);
          toast.success(t("inventory.cycles.cancel.success"));
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  // ── Session table columns ──
  const columns: ReadonlyArray<Column<LigneInventaire>> = useMemo(
    () => [
      { key: "numero", header: t("inventory.cycles.col.numero"), mono: true, cell: (l) => l.numero_lot },
      {
        key: "systeme",
        header: t("inventory.cycles.col.systeme"),
        mono: true,
        align: "end",
        cell: (l) => fmtQty(l.quantite_systeme),
      },
      {
        key: "comptee",
        header: t("inventory.cycles.col.comptee"),
        align: "end",
        cell: (l) =>
          canEditCounts ? (
            <div style={{ display: "flex", gap: "var(--nv-s-2)", alignItems: "center", justifyContent: "flex-end" }}>
              <div style={{ maxWidth: "7rem" }}>
                <Input
                  mono
                  inputMode="decimal"
                  aria-label={t("inventory.cycles.col.comptee")}
                  value={drafts[l.ligne_id] ?? (l.quantite_comptee != null ? String(l.quantite_comptee) : "")}
                  placeholder={fmtQty(l.quantite_systeme)}
                  onChange={(e) => setDrafts((d) => ({ ...d, [l.ligne_id]: e.target.value }))}
                />
              </div>
              <Button
                variant="secondary"
                loading={countMut.isPending}
                disabled={(drafts[l.ligne_id] ?? "") === ""}
                onClick={() => submitCount(l)}
              >
                {t("inventory.cycles.count.cta")}
              </Button>
            </div>
          ) : (
            <span className="nv-mono">{fmtQty(l.quantite_comptee)}</span>
          ),
      },
      {
        key: "ecart",
        header: t("inventory.cycles.col.ecart"),
        align: "end",
        cell: (l) => {
          if (l.ecart == null) return <span style={{ color: "var(--nv-slate)" }}>—</span>;
          if (l.ecart === 0) return <Badge tone="success" glyph="●">0</Badge>;
          const positive = l.ecart > 0;
          return (
            <Badge tone={positive ? "info" : "danger"} glyph={positive ? "▲" : "▼"}>
              {positive ? "+" : ""}
              {fmtQty(l.ecart)}
            </Badge>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, canEditCounts, drafts, countMut.isPending],
  );

  const header = <PageHeader title={t("inventory.cycles.title")} subtitle={t("inventory.cycles.subtitle")} />;

  if (!canRun) return <ForbiddenState requiredCapabilities={[INV_CAPS.inventaireRun]} />;
  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void catalogQuery.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (catalogQuery.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={catalogQuery.error.message} code={catalogQuery.error.code} onRetry={() => void catalogQuery.refetch()} />
      </Screen>
    );
  }

  const sv = session ? statutVisual(session.statut) : null;

  return (
    <Screen>
      {header}

      {/* Open a new cycle-count */}
      <Card title={t("inventory.cycles.open.title")}>
        <form onSubmit={submitOpen} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: "var(--nv-s-4)" }}>
              <FormField label={t("inventory.cycles.libelle")} required {...(libelleError ? { error: libelleError } : {})}>
                {(p) => (
                  <Input
                    {...p}
                    autoComplete="off"
                    value={libelle}
                    invalid={!!libelleError}
                    placeholder={t("inventory.cycles.libelle.placeholder")}
                    onChange={(e) => setLibelle(e.target.value)}
                  />
                )}
              </FormField>
              <FormField label={t("inventory.cycles.scope")}>
                {(p) => (
                  <Select
                    {...p}
                    value={scope}
                    options={SCOPES.map((s) => ({ value: s, label: t(`inventory.cycles.scope.${s}`) }))}
                    onChange={(e) => setScope(e.target.value as InventaireScope)}
                  />
                )}
              </FormField>
            </div>

            {scope === "reactif" && (
              <FormField label={t("inventory.reactif.pick")} required {...(reactifError ? { error: reactifError } : {})}>
                {(p) => (
                  <Select
                    {...p}
                    value={reactifId}
                    invalid={!!reactifError}
                    placeholder={t("inventory.reactif.pick.placeholder")}
                    options={options}
                    onChange={(e) => setReactifId(e.target.value)}
                  />
                )}
              </FormField>
            )}
            {scope === "discipline" && (
              <FormField label={t("inventory.cycles.discipline")} hint={t("inventory.cycles.discipline.hint")} required {...(disciplineError ? { error: disciplineError } : {})}>
                {(p) => (
                  <Input
                    {...p}
                    mono
                    autoComplete="off"
                    value={discipline}
                    invalid={!!disciplineError}
                    placeholder="BIOCH"
                    onChange={(e) => setDiscipline(e.target.value.toUpperCase())}
                  />
                )}
              </FormField>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" loading={openMut.isPending} disabled={!openValid}>
                {t("inventory.cycles.open.cta")}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {/* The active session */}
      {session && (
        <Card
          title={`${t("inventory.cycles.session.title")} · ${session.libelle}`}
          actions={
            sv ? (
              <Badge tone={sv.tone} glyph={sv.glyph}>
                {t(`inventory.cycles.statut.${session.statut}`)}
              </Badge>
            ) : null
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            {session.lignes.length === 0 ? (
              <EmptyState glyph="●" title={t("inventory.cycles.empty.title")} description={t("inventory.cycles.empty.desc")} />
            ) : (
              <Table<LigneInventaire>
                columns={columns}
                rows={session.lignes}
                rowKey={(l) => idStr(l.ligne_id)}
                caption={t("inventory.cycles.session.title")}
              />
            )}

            {!isTerminal && (
              <div style={{ display: "flex", gap: "var(--nv-s-2)", justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button variant="ghost" loading={cancelMut.isPending} onClick={submitCancel}>
                  {t("inventory.cycles.cancel.cta")}
                </Button>
                {session.statut === "en_cours" && (
                  <Button variant="secondary" loading={reviewMut.isPending} onClick={submitReview}>
                    {t("inventory.cycles.review.cta")}
                  </Button>
                )}
                {session.statut === "en_revue" && (
                  <Button loading={postMut.isPending} onClick={submitPost}>
                    {t("inventory.cycles.post.cta")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Post summary */}
      {postSummary && (
        <Card
          title={t("inventory.cycles.summary.title")}
          actions={
            <Badge tone={postNoop ? "info" : "success"} glyph={postNoop ? "●" : "✓"}>
              {postNoop ? t("inventory.cycles.post.noop") : t("inventory.cycles.summary.count", { n: postSummary.length })}
            </Badge>
          }
        >
          {postSummary.length === 0 ? (
            <EmptyState glyph="●" title={t("inventory.cycles.summary.title")} description={t("inventory.cycles.summary.none")} />
          ) : (
            <Table<AjustementInventaire>
              columns={[
                { key: "lot", header: t("inventory.cycles.summary.lot"), mono: true, cell: (a) => a.lot_id },
                {
                  key: "ecart",
                  header: t("inventory.cycles.col.ecart"),
                  align: "end",
                  cell: (a) => (
                    <Badge tone={a.ecart > 0 ? "info" : "danger"} glyph={a.ecart > 0 ? "▲" : "▼"}>
                      {a.ecart > 0 ? "+" : ""}
                      {fmtQty(a.ecart)}
                    </Badge>
                  ),
                },
                { key: "mv", header: t("inventory.cycles.summary.mouvement"), mono: true, align: "end", cell: (a) => a.mouvement_id },
              ]}
              rows={postSummary}
              rowKey={(a) => idStr(a.mouvement_id)}
              caption={t("inventory.cycles.summary.title")}
            />
          )}
        </Card>
      )}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
